import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import LogoutModal from '../../components/LogoutModal';
import Footer from '../../components/Footer';

interface ActivityItem {
  id: string;
  type: string;
  description: string;
  date: string;
  icon: string;
}

const UserActivity: React.FC = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('activity');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Mock activity data - this will be replaced with real API data
  const activities: ActivityItem[] = [
    {
      id: '1',
      type: 'login',
      description: 'Logged into account',
      date: new Date().toISOString(),
      icon: '🔑'
    },
    {
      id: '2',
      type: 'profile_update',
      description: 'Updated profile information',
      date: new Date(Date.now() - 86400000).toISOString(), // 1 day ago
      icon: '✏️'
    },
    {
      id: '3',
      type: 'registration',
      description: 'Account created',
      date: new Date().toISOString(),
      icon: '🎉'
    }
  ];

  const handleNavigation = (tabId: string, path: string) => {
    setActiveTab(tabId);
    window.location.href = path;
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogout = async () => {
    try {
      await logout();
      setShowLogoutModal(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const sidebarItems = [
    { id: 'home', label: 'Home', icon: '🏠', path: '/user' },
    { id: 'account', label: 'My Account', icon: '👤', path: '/user/account' },
    { id: 'activity', label: 'My Activity', icon: '📊', path: '/user/activity' }
  ];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5', fontFamily: 'Arial, sans-serif' }}>
      {/* Sidebar */}
      <div style={{ 
        position: 'fixed', 
        left: 0, 
        top: 0, 
        width: '250px', 
        height: '100vh', 
        backgroundColor: '#fff', 
        borderRight: '1px solid #ddd',
        padding: '20px',
        boxSizing: 'border-box'
      }}>
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ margin: 0, color: '#333' }}>YuvanApi</h2>
        </div>
        
        <nav>
          {sidebarItems.map(item => (
            <div key={item.id} style={{ marginBottom: '10px' }}>
              <button
                style={{
                  width: '100%',
                  padding: '12px',
                  border: 'none',
                  backgroundColor: activeTab === item.id ? '#007bff' : 'transparent',
                  color: activeTab === item.id ? 'white' : '#333',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: '14px'
                }}
                onClick={() => {
                  handleNavigation(item.id, item.path);
                  setIsMobileMenuOpen(false);
                }}
              >
                {item.icon} {item.label}
              </button>
            </div>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div style={{ marginLeft: '250px', padding: '20px' }}>
        {/* Header */}
        <div style={{ 
          backgroundColor: 'white', 
          padding: '20px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{ 
              margin: 0, 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontSize: '28px',
              fontWeight: '700',
              letterSpacing: '-0.5px'
            }}>My Activity</h1>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
                onClick={() => window.location.href = '/user'}
              >
                🏠 Dashboard
              </button>
              <button 
                style={{
                  padding: '8px 16px',
                  border: '1px solid #dc3545',
                  borderRadius: '4px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  cursor: 'pointer'
                }}
                onClick={handleLogoutClick}
              >
                🚪 Logout
              </button>
            </div>
          </div>
        </div>

        {/* Activity Content */}
        <div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', fontSize: '18px' }}>Loading...</div>
          ) : (
            <>
              {/* Activity Section */}
              <div style={{ 
                backgroundColor: 'white', 
                padding: '20px', 
                borderRadius: '8px', 
                marginBottom: '20px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                  <div style={{ 
                    width: '48px', 
                    height: '48px', 
                    backgroundColor: '#007bff', 
                    borderRadius: '8px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontSize: '24px' 
                  }}>
                    📋
                  </div>
                  <h2 style={{ margin: 0, color: '#333', fontSize: '24px', fontWeight: 'bold' }}>Recent Activity</h2>
                </div>
                
                {activities.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                    <div style={{ fontSize: '64px', marginBottom: '20px', opacity: 0.5 }}>📭</div>
                    <h3 style={{ fontSize: '24px', fontWeight: 'bold', color: '#666', marginBottom: '10px' }}>No Activity Yet</h3>
                    <p style={{ color: '#888' }}>Your account activity will appear here</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {activities.map((activity, index) => {
                      const getBgColor = (type: string) => {
                        if (type === 'login') return { backgroundColor: '#e8f5e8', color: '#2d5a2d', borderColor: '#4caf50' };
                        if (type === 'profile_update') return { backgroundColor: '#e3f2fd', color: '#1565c0', borderColor: '#2196f3' };
                        return { backgroundColor: '#fff3e0', color: '#e65100', borderColor: '#ff9800' };
                      };

                      const typeStyle = getBgColor(activity.type);

                      return (
                        <div 
                          key={activity.id} 
                          style={{ 
                            backgroundColor: 'white', 
                            border: '1px solid #eee', 
                            borderRadius: '8px', 
                            padding: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '15px'
                          }}
                        >
                          <div style={{ 
                            width: '56px', 
                            height: '56px', 
                            backgroundColor: typeStyle.backgroundColor, 
                            borderRadius: '8px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            fontSize: '24px',
                            border: `2px solid ${typeStyle.borderColor}`
                          }}>
                            {activity.icon}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                              <h3 style={{ margin: 0, fontWeight: 'bold', color: '#333', fontSize: '16px' }}>
                                {activity.description}
                              </h3>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#666' }}>
                                <span>🕐</span>
                                <span>{formatDate(activity.date)}</span>
                                <span>at</span>
                                <span style={{ fontWeight: 'bold', color: '#007bff' }}>{formatTime(activity.date)}</span>
                              </div>
                            </div>
                            <div>
                              <span style={{ 
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                gap: '6px', 
                                padding: '6px 12px', 
                                borderRadius: '16px', 
                                fontSize: '12px', 
                                fontWeight: 'bold', 
                                textTransform: 'uppercase',
                                backgroundColor: typeStyle.backgroundColor,
                                color: typeStyle.color,
                                border: `1px solid ${typeStyle.borderColor}`
                              }}>
                                <span style={{ 
                                  width: '6px', 
                                  height: '6px', 
                                  borderRadius: '50%', 
                                  backgroundColor: typeStyle.color 
                                }}></span>
                                {activity.type.replace('_', ' ')}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Activity Stats */}
              <div style={{ 
                backgroundColor: 'white', 
                padding: '20px', 
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                  <div style={{ 
                    width: '48px', 
                    height: '48px', 
                    backgroundColor: '#28a745', 
                    borderRadius: '8px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontSize: '24px' 
                  }}>
                    📈
                  </div>
                  <h3 style={{ margin: 0, color: '#333', fontSize: '24px', fontWeight: 'bold' }}>Activity Summary</h3>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                  {[
                    { 
                      icon: '📊', 
                      label: 'Total Activities', 
                      value: activities.length,
                      backgroundColor: '#e1bee7',
                      color: '#4a148c',
                      description: 'All events'
                    },
                    { 
                      icon: '🔑', 
                      label: 'Login Events', 
                      value: activities.filter(a => a.type === 'login').length,
                      backgroundColor: '#c8e6c9',
                      color: '#1b5e20',
                      description: 'Sign-ins'
                    },
                    { 
                      icon: '✏️', 
                      label: 'Profile Updates', 
                      value: activities.filter(a => a.type === 'profile_update').length,
                      backgroundColor: '#bbdefb',
                      color: '#0d47a1',
                      description: 'Modifications'
                    }
                  ].map((stat, index) => (
                    <div 
                      key={index} 
                      style={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #eee', 
                        borderRadius: '8px', 
                        padding: '20px',
                        textAlign: 'center'
                      }}
                    >
                      <div style={{ 
                        width: '80px', 
                        height: '80px', 
                        backgroundColor: stat.backgroundColor, 
                        borderRadius: '12px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        fontSize: '32px',
                        margin: '0 auto 15px auto'
                      }}>
                        {stat.icon}
                      </div>
                      <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#333', marginBottom: '8px' }}>
                        {stat.value}
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#666', textTransform: 'uppercase', marginBottom: '4px' }}>
                        {stat.label}
                      </div>
                      <div style={{ fontSize: '12px', color: '#888' }}>
                        {stat.description}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <Footer />
      </div>

      {/* Modals */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
      />
    </div>
  );
};

export default UserActivity;
